import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from '@/components/feature/HeroSection';
import CategoryGrid from '@/components/feature/CategoryGrid';
import TrustStats from '@/components/feature/TrustStats';
import ArticlePreview from '@/components/feature/ArticlePreview';
import TestimonialSection from '@/components/feature/TestimonialSection';
import CTASection from '@/components/feature/CTASection';
import { mainSiteUrl } from '@/mocks/categories';
import { SITE_URL } from '@/constants';

const siteUrl = SITE_URL;

export default function Home() {
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'SugargooSpreadsheet.link',
      url: siteUrl,
      description:
        'The ultimate Sugargoo Spreadsheet directory for 2026. Browse curated replica finds, verified QC photos, trusted seller links, and comprehensive buying guides.',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}/guide/{search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'SugargooSpreadsheet.link',
      url: siteUrl,
      description:
        'Independent Sugargoo Spreadsheet resource providing curated replica finds, verified QC photos, and trusted seller links.',
      sameAs: [siteUrl],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Sugargoo Spreadsheet 2026 | Best Finds, QC Photos & Trusted Links',
      description:
        'Discover the ultimate Sugargoo Spreadsheet directory for 2026. Browse curated replica finds, verified QC photos, trusted seller links, and comprehensive buying guides. Your go-to resource for Sugargoo shopping.',
      url: `${siteUrl}/`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'SugargooSpreadsheet.link',
        url: siteUrl,
      },
      about: {
        '@type': 'Thing',
        name: 'Sugargoo Spreadsheet',
        description:
          'Curated community-maintained product catalogs for Sugargoo shoppers featuring verified QC photos, trusted seller links, and organized category navigation.',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${siteUrl}/`,
          },
        ],
      },
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navbar />
      <main>
        <HeroSection />
        <CategoryGrid />
        <TrustStats />
        <ArticlePreview />
        <TestimonialSection />
        <CTASection />

        {/* Product category quick links section */}
        <section className="w-full bg-background-50 py-14 md:py-20" data-product-shop>
          <div className="w-full px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
                Browse All Categories
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10 max-w-lg mx-auto">
                Explore our complete directory of Sugargoo spreadsheet categories. Each link takes you directly to curated product listings on our main website.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop>
                <a
                  href="https://kaspreadsheet.com/category-911-Shoes.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-footprint-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Shoes</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-t-shirt-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Hoodies/Sweaters</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-913-T-Shirts.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-shirt-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">T-Shirts</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-914-Jackets.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-windy-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Jackets</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-915-Pants%2FShorts.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-pantone-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Pants/Shorts</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-916-Headwear.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-emotion-happy-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Headwear</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-917-Sets.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-stack-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Sets</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-918-underwear%2Funderpants.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-shirt-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Underwear</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-919-Jersey.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-user-star-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Jersey</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-920-Accessories.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-vip-crown-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Accessories</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/category-921-Bags.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-shopping-bag-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Bags</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/search-922/name/Bags.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-link text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Belts</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/search-923/name/Bags.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-drop-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Perfumes</span>
                </a>
                <a
                  href="https://kaspreadsheet.com/search-924/name/Bags.html"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-3.5 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer group"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                    <i className="ri-more-line text-foreground-600 group-hover:text-primary-500"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">Other Stuff</span>
                </a>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Sugargoo Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-foreground-200 text-foreground-800 rounded-full text-sm font-semibold hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}